import classNames from 'classnames';
import React, { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import { formatExtrasPriceDetails } from '@/components/organisms/booking-price-breakdown/booking-price-breakdown.utils';
import BookingPriceDetailsModal from '@/components/organisms/booking-price-details-modal/booking-price-details-modal';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import BookingPriceDiscountModal from '../booking-price-discount-modal/booking-price-discount-modal';
import styles from './booking-price-breakdown.module.scss';

export interface BookingPriceBreakdownProps {
  /** The booking price details */
  bookingPriceDetails?: Content.Components['BookingPriceDetailsDto'];
  /** The class name to apply */
  className?: string;
  /** The identifiers for the discount code query. */
  identifiers?: {
    bookingStateId: string;
    id: string;
    pageId: string;
  };
  /** The installment information of the booking payment */
  installments?: Content.Components['MyTripUpcomingPaymentModuleInstallmentDto'][];
  /** Indicates whether the component is fetching data */
  isFetching?: boolean;
  /** Indicates whether the price details are shown in a modal */
  isInModal?: boolean;
  /** Indicates whether the data is being fetched */
  isLoading?: boolean;
  /** Optional limit on number of items to display */
  limit?: number;
}

const BookingPriceBreakdown = ({
  className,
  bookingPriceDetails,
  identifiers,
  installments,
  isFetching = false,
  isInModal = false,
  isLoading = false,
  limit = 0,
}: BookingPriceBreakdownProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const [isOpenBookingPriceDetails, setIsOpenBookingPriceDetails] =
    useState(false);
  const [isOpenDiscountModal, setIsOpenDiscountModal] = useState(false);

  const items: React.ReactNode[] = [];
  const formattedBookingPriceDetails = formatExtrasPriceDetails(
    bookingPriceDetails?.extrasPriceDetails,
  );

  if (isLoading || isFetching) {
    return (
      <div
        className={classNames(styles.component, className, styles.isLoading)}
      >
        <div className={styles.topSection}>
          {isFetching && bookingPriceDetails?.headline ? (
            <Typography className={styles.headline} tagStyle="headingSmall">
              {bookingPriceDetails.headline}
            </Typography>
          ) : (
            <SkeletonLoader
              className={classNames(styles.headline, styles.loader)}
              height="24px"
              width="75px"
            />
          )}

          <div className={styles.content}>
            {Array.from({ length: 8 }).map((_, index) => {
              const priceBaseWidth = 50;
              const productNameBaseWidth = 120;
              const productNameWidth =
                index % 2 === 0
                  ? productNameBaseWidth - index * 4
                  : productNameBaseWidth + index * 4;

              const priceWidth =
                index % 2 === 0
                  ? priceBaseWidth - index * 2
                  : priceBaseWidth + index * 2;

              return (
                <div key={index} className={styles.item}>
                  <SkeletonLoader
                    className={classNames(styles.label, styles.loader)}
                    height="22px"
                    width={`${productNameWidth}px`}
                  />

                  <SkeletonLoader
                    className={classNames(styles.price, styles.loader)}
                    height="22px"
                    width={`${priceWidth}px`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.horizontalDivider} />

        <div className={styles.bottomSection}>
          {isFetching ? (
            <Typography className={styles.priceText} tagStyle="labelMedium">
              {t('items.modules.bookingFlow.inTotal')}
            </Typography>
          ) : (
            <SkeletonLoader
              className={classNames(styles.headline, styles.loader)}
              height="24px"
              width="75px"
            />
          )}

          <div className={styles.priceWrapper}>
            <SkeletonLoader
              className={classNames(styles.price, styles.loader)}
              height="24px"
              width="75px"
            />
          </div>
        </div>
      </div>
    );
  }

  if (!bookingPriceDetails || !formattedBookingPriceDetails) {
    return null;
  }

  // 1. Base Price
  items.push(
    <div key="base-price" className={styles.item}>
      <Typography className={styles.label} tagStyle="bodyMedium">
        {t('items.modules.bookingFlow.basePrice')}
      </Typography>

      <Typography className={styles.price} tagStyle="bodyMedium">
        {currencyFormatter(bookingPriceDetails.normalPrice)}
      </Typography>
    </div>,
  );

  // 2. Travel (section + entries)
  items.push(
    <div key="travel-header" className={styles.item}>
      <Typography className={styles.label} tagStyle="bodyMedium">
        {t('items.modules.bookingFlow.productTypeTravel')}
      </Typography>

      {formattedBookingPriceDetails.travel.length ? null : (
        <Typography tagStyle="bodyMedium"> {`-`} </Typography>
      )}
    </div>,
  );

  formattedBookingPriceDetails.travel.forEach((travel, travelIndex) => {
    items.push(
      <div
        key={`travel-${travelIndex}`}
        className={classNames(styles.item, styles.subItem)}
      >
        <Typography className={styles.label} tagStyle="bodyMedium">
          {`x${travel.quantity} ${travel.name}`}
        </Typography>

        <Typography
          className={classNames(styles.price, styles.noWrap)}
          tagStyle="bodyMedium"
        >
          {travel.displayPrice
            ? travel.displayPrice
            : currencyFormatter(travel.amount)}
        </Typography>
      </div>,
    );

    if (travel.packageProducts?.length) {
      items.push(
        <ul
          key={`travel-package-products-${travelIndex}`}
          className={classNames(styles.packageContentList, styles.subItem)}
        >
          {travel.packageProducts.map((packageProduct, index) => (
            <li
              key={`travel-package-product-${travelIndex}-${index}`}
              className={styles.packageContentItem}
            >
              <Typography tagStyle="bodyMedium">{packageProduct}</Typography>
            </li>
          ))}
        </ul>,
      );
    }
  });

  // 3. Stay (section + entries)
  items.push(
    <div key="travel-stay" className={styles.item}>
      <Typography className={styles.label} tagStyle="bodyMedium">
        {t('items.modules.bookingFlow.productTypeStay')}
      </Typography>

      {formattedBookingPriceDetails.stay.length ? null : (
        <Typography tagStyle="bodyMedium"> {`-`} </Typography>
      )}
    </div>,
  );

  formattedBookingPriceDetails.stay.forEach((stay, stayIndex) => {
    items.push(
      <div
        key={`stay-${stayIndex}`}
        className={classNames(styles.item, styles.subItem)}
      >
        <Typography className={styles.label} tagStyle="bodyMedium">
          {`x${stay.quantity} ${stay.name}`}
        </Typography>

        <Typography
          className={classNames(styles.price, styles.noWrap)}
          tagStyle="bodyMedium"
        >
          {stay.displayPrice
            ? stay.displayPrice
            : currencyFormatter(stay.amount)}
        </Typography>
      </div>,
    );

    if (stay.packageProducts?.length) {
      items.push(
        <ul
          key={`stay-package-products-${stayIndex}`}
          className={classNames(styles.packageContentList, styles.subItem)}
        >
          {stay.packageProducts.map((packageProduct, index) => (
            <li
              key={`stay-package-product-${stayIndex}-${index}`}
              className={styles.packageContentItem}
            >
              <Typography tagStyle="bodyMedium">{packageProduct}</Typography>
            </li>
          ))}
        </ul>,
      );
    }
  });

  // 4. Discounts
  bookingPriceDetails.discountPriceDetails.forEach(
    (discount, discountIndex) => {
      items.push(
        <div key={`discount-${discountIndex}`} className={styles.item}>
          <Typography
            className={classNames(styles.label, styles.discount)}
            tagStyle="bodyMedium"
          >
            {`x${discount.quantity} ${discount.name}`}
          </Typography>

          <Typography
            className={classNames(styles.price, styles.discount, styles.noWrap)}
            tagStyle="bodyMedium"
          >
            {`- ${currencyFormatter(discount.amount)}`}
          </Typography>
        </div>,
      );
    },
  );

  const visibleItems = limit ? items.slice(0, limit) : items;
  const hasHiddenItems = visibleItems.length < items.length;

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isInModal]: isInModal,
        [styles.hasHiddenItems]: hasHiddenItems,
      })}
    >
      <div className={styles.topSection}>
        {bookingPriceDetails.headline && (
          <Typography className={styles.headline} tagStyle="headingSmall">
            {bookingPriceDetails.headline}
          </Typography>
        )}

        <div className={styles.content}>
          <div className={styles.items}>{visibleItems}</div>

          {hasHiddenItems && (
            <Link
              className={styles.seeAll}
              onClick={() => {
                setIsOpenBookingPriceDetails(true);
              }}
            >
              <Typography tagStyle="bodyMedium">
                {t('items.modules.bookingFlow.seeAll')}
              </Typography>
            </Link>
          )}
        </div>
      </div>

      <Link
        className={styles.discountCode}
        onClick={() => setIsOpenDiscountModal(true)}
      >
        <Typography tagStyle="bodyMedium">
          {`+ ${t('items.modules.bookingFlow.addDiscountCode')}`}
        </Typography>
      </Link>

      <div className={styles.horizontalDivider} />

      <div className={styles.bottomSection}>
        <div className={styles.totalPriceWrapper}>
          <Typography className={styles.priceText} tagStyle="labelMedium">
            {t('items.modules.bookingFlow.inTotal')}
          </Typography>

          <div className={styles.priceWrapper}>
            <Typography className={styles.totalPrice} tagStyle="priceMedium">
              {currencyFormatter(bookingPriceDetails.totalPrice)}
            </Typography>
          </div>
        </div>

        {installments && (
          <div className={styles.installments}>
            {installments.map((installment, index) => (
              <div key={index} className={styles.installment}>
                <Typography
                  className={styles.installmentHeadline}
                  tagStyle="bodySmall"
                >
                  {installment.headline}
                </Typography>

                <Typography
                  className={styles.installmentPrice}
                  tagStyle="bodyMediumBold"
                >
                  {installment.isPaid
                    ? currencyFormatter(installment.fullPrice)
                    : currencyFormatter(installment.outstandingPrice)}
                </Typography>

                {installment.isPaid && (
                  <div className={styles.paymentStatus}>
                    <div className={styles.iconContainer}>
                      <Icon name="CheckMark" size={8} />
                    </div>

                    <Typography tagStyle="bodySmallBold">
                      {t('items.modules.myTripUpcomingPayment.paid')}
                    </Typography>
                  </div>
                )}

                {index < installments.length - 1 && (
                  <div className={styles.verticalDivider} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {hasHiddenItems && (
        <BookingPriceDetailsModal
          bookingPriceDetails={bookingPriceDetails}
          identifiers={
            identifiers ?? { bookingStateId: '', id: '', pageId: '' }
          }
          isOpen={isOpenBookingPriceDetails}
          onClose={setIsOpenBookingPriceDetails}
        />
      )}

      <BookingPriceDiscountModal
        addedDiscountCode={bookingPriceDetails.addedDiscountCode ?? ''}
        description={bookingPriceDetails.discountCodeDescription}
        headline={bookingPriceDetails.discountCodeHeadline}
        identifiers={identifiers ?? { bookingStateId: '', id: '', pageId: '' }}
        isOpen={isOpenDiscountModal}
        onClose={setIsOpenDiscountModal}
      />
    </div>
  );
};

BookingPriceBreakdown.displayName = 'BookingPriceBreakdown';

export default BookingPriceBreakdown;
